import {
  getGooglePlacesStatus,
  subscribeToGooglePlacesStatus,
  type GooglePlacesStatus,
} from '@choco-admin/utils';
import { useState, useCallback, useEffect, useRef } from 'react';

// Local interface for Google Places predictions
interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

export interface PlaceDetails {
  formatted_address?: string;
  name: string;
  place_id: string;
  geometry?: {
    location: {
      lat(): number;
      lng(): number;
    };
  };
  utc_offset_minutes?: number;
  address_components?: Array<{
    types: string[];
    long_name: string;
    short_name: string;
  }>;
}

interface UseGooglePlacesProps {
  onPlaceDetailsRequestSuccess: (placeDetails: PlaceDetails) => void;
  onPlaceDetailsRequestError: (error: Error) => void;
}

export interface FetchPredictionsParams {
  searchTerm: string;
  placeTypes?: string[];
}

export interface FetchPlaceDetailsParams {
  placeId: string;
}

/** A single point, in the shape the Maps SDK accepts and returns. */
export interface GoogleLatLngLiteral {
  lat: number;
  lng: number;
}

/** A point read back off a polygon path. */
export interface GoogleLatLng {
  lat(): number;
  lng(): number;
}

/**
 * Google's mutable list type, as returned by `Polygon.getPath()`.
 *
 * Vertex edits fire on the path rather than on the polygon, so this is also
 * where `set_at` / `insert_at` / `remove_at` are observed.
 */
export interface GoogleMVCArray {
  getLength(): number;
  getAt(index: number): GoogleLatLng;
  addListener(event: string, handler: () => void): GoogleMapsListener;
}

export interface GoogleMapsListener {
  remove(): void;
}

/** The polygon methods the delivery-zone editor calls. */
export interface GooglePolygon {
  setMap(map: GoogleMapInstance | null): void;
  setEditable(editable: boolean): void;
  setOptions(options: { strokeColor?: string; fillColor?: string }): void;
  getPath(): GoogleMVCArray;
  setPath(path: GoogleLatLngLiteral[]): void;
  addListener(event: string, handler: () => void): GoogleMapsListener;
}

export interface GoogleLatLngBounds {
  extend(point: GoogleLatLngLiteral): void;
  isEmpty(): boolean;
}

/** A map click, which carries the clicked position. */
export interface GoogleMapMouseEvent {
  latLng?: GoogleLatLng;
}

/** The handful of map methods this app calls. */
export interface GoogleMapInstance {
  setCenter(position: GoogleLatLngLiteral): void;
  setZoom(zoom: number): void;
  fitBounds(bounds: GoogleLatLngBounds): void;
  setOptions(options: { draggableCursor?: string }): void;
  /**
   * Used to place delivery-zone corners. Google removed `DrawingManager` in Maps
   * JavaScript API 3.65, so drawing is built from map clicks instead.
   */
  addListener(
    event: 'click',
    handler: (event: GoogleMapMouseEvent) => void
  ): GoogleMapsListener;
}

// Declare Google Maps types. One declaration site for the whole SDK surface —
// a second `declare global` for `window.google` would collide with this one.
declare global {
  interface Window {
    google?: {
      maps: {
        /** Interactive map, used to pin a buyer's stored coordinates. */
        Map: new (
          container: HTMLElement,
          options: {
            center: { lat: number; lng: number };
            zoom: number;
            disableDefaultUI?: boolean;
            streetViewControl?: boolean;
            mapTypeControl?: boolean;
          }
        ) => GoogleMapInstance;
        Marker: new (options: {
          position: { lat: number; lng: number };
          map: GoogleMapInstance;
          title?: string;
        }) => { setMap(map: GoogleMapInstance | null): void };
        /** Delivery-zone outlines, drawn and reshaped on the supplier form. */
        Polygon: new (options: {
          paths?: GoogleLatLngLiteral[];
          map?: GoogleMapInstance;
          editable?: boolean;
          clickable?: boolean;
          strokeColor?: string;
          strokeWeight?: number;
          fillColor?: string;
          fillOpacity?: number;
        }) => GooglePolygon;
        LatLngBounds: new () => GoogleLatLngBounds;
        /** The outline in progress while a delivery zone is being drawn. */
        Polyline: new (options: {
          path?: GoogleLatLngLiteral[];
          map?: GoogleMapInstance;
          strokeColor?: string;
          strokeWeight?: number;
        }) => { setMap(map: GoogleMapInstance | null): void };
        places: {
          AutocompleteService: new () => {
            getPlacePredictions(
              request: {
                input: string;
                types?: string[];
              },
              callback: (
                predictions: PlacePrediction[] | null,
                status: string
              ) => void
            ): void;
          };
          PlacesService: new (div: HTMLElement) => {
            getDetails(
              request: {
                placeId: string;
                fields: string[];
              },
              callback: (place: PlaceDetails | null, status: string) => void
            ): void;
          };
          PlacesServiceStatus: {
            OK: string;
            ZERO_RESULTS: string;
          };
        };
      };
    };
  }
}

/**
 * Why a finding is unavailable, so the UI can say something actionable rather
 * than spin (CRM-359).
 */
export type PlacesUnavailableReason =
  | 'auth'
  | 'unavailable'
  | 'timeout'
  | 'request';

/**
 * The Places services take a callback that is simply never invoked when the SDK
 * refuses a call (rejected key, disabled API, revoked authorization). Nothing in
 * the SDK reports that back, so every call is raced against a timer.
 */
const PREDICTIONS_TIMEOUT_MS = 8000;
const PLACE_DETAILS_TIMEOUT_MS = 10000;

const statusToReason = (
  status: GooglePlacesStatus
): PlacesUnavailableReason | null => {
  if (status === 'auth-failed') return 'auth';
  if (status === 'load-failed' || status === 'unconfigured') {
    return 'unavailable';
  }
  return null;
};

export const useGooglePlaces = ({
  onPlaceDetailsRequestSuccess,
  onPlaceDetailsRequestError,
}: UseGooglePlacesProps) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [predictionsError, setPredictionsError] =
    useState<PlacesUnavailableReason | null>(null);
  const [loadingPlaceDetails, setLoadingPlaceDetails] = useState(false);

  // Only the newest search may write results — predictions race on every
  // keystroke and the SDK does not cancel superseded calls.
  const requestIdRef = useRef(0);
  // Details requests get their own counter: selecting B while A's details are
  // still in flight must not let A's late callback overwrite B's coordinates.
  const detailsRequestIdRef = useRef(0);
  const predictionsTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const detailsTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // A search typed while the SDK is still loading is replayed once it is ready.
  const pendingSearchRef = useRef<FetchPredictionsParams>();

  /**
   * Abandon any in-flight details lookup. A selection stops being what the user
   * wants the moment they type again, clear the field, or pick something else —
   * and its late answer must not be able to write itself back into the form.
   */
  const invalidatePlaceDetails = useCallback(() => {
    detailsRequestIdRef.current += 1;
    if (detailsTimerRef.current) {
      clearTimeout(detailsTimerRef.current);
      detailsTimerRef.current = undefined;
    }
    setLoadingPlaceDetails(false);
  }, []);

  const clearPredictionsTimer = useCallback(() => {
    if (predictionsTimerRef.current) {
      clearTimeout(predictionsTimerRef.current);
      predictionsTimerRef.current = undefined;
    }
  }, []);

  const runPredictionsRequest = useCallback(
    ({ searchTerm, placeTypes }: FetchPredictionsParams) => {
      if (!window.google?.maps?.places) return;

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const isStale = () => requestId !== requestIdRef.current;

      setLoadingPredictions(true);
      setPredictionsError(null);
      clearPredictionsTimer();

      // The SDK can accept the call and never answer (see the timeout constants
      // above); without this the spinner would run forever.
      predictionsTimerRef.current = setTimeout(() => {
        if (isStale()) return;
        setPredictions([]);
        setLoadingPredictions(false);
        setPredictionsError(
          statusToReason(getGooglePlacesStatus()) ?? 'timeout'
        );
      }, PREDICTIONS_TIMEOUT_MS);

      try {
        const service = new window.google.maps.places.AutocompleteService();
        const request = {
          input: searchTerm,
          types: placeTypes || ['establishment'],
        };

        service.getPlacePredictions(request, (results, status) => {
          if (isStale()) return;
          clearPredictionsTimer();
          setLoadingPredictions(false);
          const statusCodes = window?.google?.maps?.places?.PlacesServiceStatus;
          if (status === statusCodes?.OK && results) {
            setPredictions(results);
          } else if (status === statusCodes?.ZERO_RESULTS) {
            // No matches is a valid outcome, not an error
            setPredictions([]);
          } else {
            // Log the status for debugging
            // eslint-disable-next-line no-console
            console.warn('Google Places API error:', status);
            setPredictions([]);
            setPredictionsError('request');
          }
        });
      } catch (error) {
        // The API loaded but is unusable (e.g. authorization was revoked
        // after load) — stop the spinner and show the error state (#106).
        // eslint-disable-next-line no-console
        console.warn('Google Places API error:', error);
        clearPredictionsTimer();
        setPredictions([]);
        setLoadingPredictions(false);
        setPredictionsError(
          statusToReason(getGooglePlacesStatus()) ?? 'request'
        );
      }
    },
    [clearPredictionsTimer]
  );

  const fetchPlacePredictions = useCallback(
    (params: FetchPredictionsParams) => {
      // Typing is the user telling us the previous pick is not the one.
      invalidatePlaceDetails();

      if (!params.searchTerm.trim()) {
        requestIdRef.current += 1;
        pendingSearchRef.current = undefined;
        clearPredictionsTimer();
        setPredictions([]);
        setLoadingPredictions(false);
        setPredictionsError(null);
        return;
      }

      const reason = statusToReason(getGooglePlacesStatus());
      if (reason) {
        requestIdRef.current += 1;
        pendingSearchRef.current = undefined;
        clearPredictionsTimer();
        setPredictions([]);
        setLoadingPredictions(false);
        setPredictionsError(reason);
        return;
      }

      // Typed before the SDK finished loading: hold the term and show the
      // spinner — the status subscription replays it once ready, instead of
      // reporting an error for what is only a slow load (#106). The wait is
      // itself bounded: a script that neither loads nor fires `onerror` (hung
      // request, blocked by a proxy) would otherwise leave this spinner
      // running forever — the exact failure this hook exists to prevent.
      if (!window.google?.maps?.places) {
        pendingSearchRef.current = params;
        setPredictionsError(null);
        setLoadingPredictions(true);
        clearPredictionsTimer();
        predictionsTimerRef.current = setTimeout(() => {
          pendingSearchRef.current = undefined;
          setLoadingPredictions(false);
          setPredictionsError(
            statusToReason(getGooglePlacesStatus()) ?? 'unavailable'
          );
        }, PREDICTIONS_TIMEOUT_MS);
        return;
      }

      pendingSearchRef.current = undefined;
      runPredictionsRequest(params);
    },
    [clearPredictionsTimer, runPredictionsRequest, invalidatePlaceDetails]
  );

  // React to SDK status changes: replay a queued search once ready, and stop
  // waiting the moment the SDK reports it will never answer.
  useEffect(() => {
    const unsubscribe = subscribeToGooglePlacesStatus((status) => {
      const reason = statusToReason(status);
      if (reason) {
        pendingSearchRef.current = undefined;
        clearPredictionsTimer();
        invalidatePlaceDetails();
        setPredictions([]);
        setLoadingPredictions(false);
        setPredictionsError(reason);
        return;
      }

      if (status === 'ready' && pendingSearchRef.current) {
        const pending = pendingSearchRef.current;
        pendingSearchRef.current = undefined;
        runPredictionsRequest(pending);
      }
    });

    return unsubscribe;
  }, [clearPredictionsTimer, runPredictionsRequest, invalidatePlaceDetails]);

  // Never leave a timer (or a spinner) behind on unmount.
  useEffect(
    () => () => {
      requestIdRef.current += 1;
      detailsRequestIdRef.current += 1;
      if (predictionsTimerRef.current)
        clearTimeout(predictionsTimerRef.current);
      if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);
    },
    []
  );

  const fetchPlaceDetails = useCallback(
    ({ placeId }: FetchPlaceDetailsParams) => {
      const reason = statusToReason(getGooglePlacesStatus());
      if (reason || !window.google?.maps?.places) {
        setLoadingPlaceDetails(false);
        setPredictionsError(reason ?? 'unavailable');
        onPlaceDetailsRequestError(
          new Error('Google Places API not available')
        );
        return;
      }

      setLoadingPlaceDetails(true);

      detailsRequestIdRef.current += 1;
      const detailsRequestId = detailsRequestIdRef.current;

      let settled = false;
      const settle = () => {
        // Stale: a newer selection has taken over, so this response must not
        // reach the form or touch shared state.
        if (detailsRequestId !== detailsRequestIdRef.current) return false;
        if (settled) return false;
        settled = true;
        if (detailsTimerRef.current) {
          clearTimeout(detailsTimerRef.current);
          detailsTimerRef.current = undefined;
        }
        setLoadingPlaceDetails(false);
        return true;
      };

      // Same silent-hang exposure as predictions: a rejected key means this
      // callback never fires, which would leave the row stuck mid-selection.
      detailsTimerRef.current = setTimeout(() => {
        if (!settle()) return;
        setPredictionsError(
          statusToReason(getGooglePlacesStatus()) ?? 'timeout'
        );
        onPlaceDetailsRequestError(
          new Error('Timed out fetching place details')
        );
      }, PLACE_DETAILS_TIMEOUT_MS);

      try {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        const request = {
          placeId,
          fields: [
            'name',
            'formatted_address',
            'geometry',
            'place_id',
            'address_components',
            'utc_offset_minutes',
          ],
        };

        service.getDetails(request, (place, status) => {
          if (!settle()) return;
          if (
            status === window?.google?.maps?.places?.PlacesServiceStatus?.OK &&
            place
          ) {
            onPlaceDetailsRequestSuccess(place);
          } else {
            // eslint-disable-next-line no-console
            console.warn('Google Places API error:', status);
            setPredictionsError('request');
            onPlaceDetailsRequestError(
              new Error(`Failed to fetch place details: ${status}`)
            );
          }
        });
      } catch (error) {
        if (!settle()) return;
        // eslint-disable-next-line no-console
        console.warn('Google Places API error:', error);
        setPredictionsError(
          statusToReason(getGooglePlacesStatus()) ?? 'request'
        );
        onPlaceDetailsRequestError(
          error instanceof Error
            ? error
            : new Error('Failed to fetch place details')
        );
      }
    },
    [onPlaceDetailsRequestSuccess, onPlaceDetailsRequestError]
  );

  return {
    predictions,
    loadingPredictions,
    predictionsError,
    loadingPlaceDetails,
    fetchPlacePredictions,
    fetchPlaceDetails,
  };
};
